// src/components/profile/AddressesTab.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Shield, Plus, MapPin, Package, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import AddressForm from '../../components/address/AddressForm';
import AddressCard from '../../components/address/AddressCard';
import { useAddress } from '../../contexts/AddressContext';
import type { Address, City } from '../../types';
import toast, { showToast } from '../../utils/toast';
import useLocationCapture from '../../utils/locationCapture';
import { formatCoord } from '../../utils/utilityFunctions';
import addressAPI from '../../api/addresses';

const AddressesTab: React.FC = () => {
    const { user } = useAuth();
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [citiesData, setCitiesData] = useState<City[]>([]);
    const [activeTab, setActiveTab] = useState<'profile' | 'addresses' | 'security' | 'orders'>('profile');
    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'addresses', label: 'Addresses', icon: MapPin },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'orders', label: 'Orders', icon: Package },
    ];
    const { captureLocation } = useLocationCapture();
    const {
        addresses,
        deleteAddress: contextDeleteAddress,
        setDefaultAddress: contextSetDefaultAddress,
        addAddress: contextAddAddress,
        updateAddress: contextUpdateAddress,
        isLoading: isAddressesLoading,
        setIsLoading: setAddressLoading,
        fetchAddresses,
        fetchAddressesIfNeeded, // Stops glitching when switching to Addresses tab
        updateAddressCoordinates: contextUpdateAddressCoordinates,
    } = useAddress();

    // Sort addresses before rendering
    const sortedAddresses = [...(addresses || [])].sort((a, b) => {
        // Default addresses first
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;

        // Then by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const handleAddAddress = () => {
        setEditingAddress(null);
        setShowAddressForm(true);
    };

    const handleEditAddress = (address: Address) => {
        setEditingAddress(address);
        setShowAddressForm(true);
    };

    const handleCancelAddress = () => {
        setShowAddressForm(false);
        setEditingAddress(null);
    };

    const setDefaultAddress = async (addressId: number) => {
        try {
            // Use context method which handles UI update automatically
            await contextSetDefaultAddress(addressId);
            toast.success('Default address updated successfully');
        } catch (error) {
            console.error('Failed to set default address:', error);
            toast.error('Failed to update default address');
        }
    };

    const handleUpdateCoordinates = async (addressId: number) => {
        try {
            const coords = await captureLocation();

            const response = await contextUpdateAddressCoordinates(addressId, coords);
            if (response.data) {
                toast.success('Location saved successfully');
                // Refresh addresses
                fetchAddresses();
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    // Delete address
    const handleDeleteAddress = async (addressId: number) => {
        try {
            // Use context method which handles UI update automatically
            await contextDeleteAddress(addressId);
            toast.success('Address deleted successfully');
        } catch (error) {
            console.error('Failed to delete address:', error);
            toast.error('Failed to delete address');
        }
    };

    const handleSubmitAddress = async (addressData: any) => {

        addressData.longitude &&= formatCoord(addressData.longitude);
        addressData.latitude &&= formatCoord(addressData.latitude);
        addressData.coordinates_saved_at = addressData.latitude ? new Date().toISOString() : null;

        try {
            if (editingAddress) {
                // Use context update method
                await contextUpdateAddress(editingAddress.id, addressData);
                toast.success('Address updated successfully');
            } else {
                // Use context add method
                await contextAddAddress(addressData);
                toast.success('Address added successfully');
            }

            // Reset form
            setEditingAddress(null);
            setShowAddressForm(false);
        } catch (error: any) {
            console.error('Failed to save address:', error);
            toast.error(error.error || 'Failed to save address');
        }
    };

    // Fetch cities on component mount
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const response = await addressAPI.getCities();
                if (response.data) {
                    setCitiesData(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch cities:', error);
                // Set default cities as fallback
                setCitiesData([
                    { id: 1, name: 'Port of Spain', delivery_fee: 40, is_active: true },
                    { id: 2, name: 'San Fernando', delivery_fee: 40, is_active: true },
                    // Add more if needed
                ]);
            }
        };
        fetchCities();
        fetchAddressesIfNeeded();
    }, []);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-start">
                    <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                    <div>
                        <h3 className="font-medium text-gray-900 mb-1">Address Verification</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            You need at least one <strong>verified HOME address</strong>.
                            Tap "Save Location" to verify your address.
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                            <div className="flex items-center">
                                <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                                <span>Verified home address shows with green badge</span>
                            </div>
                            <div className="flex items-center">
                                <Clock className="h-3 w-3 text-amber-600 mr-1" />
                                <span>Pending verification shows with amber badge</span>
                            </div>
                            <div className="flex items-center">
                                <AlertCircle className="h-3 w-3 text-red-600 mr-1" />
                                <span>Unverified home address requires action</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-6 py-6">
                {/* Address Management Section */}
                <div className="mt-8">
                    <div className="mb-6">
                        {/* Desktop layout */}
                        <div className="hidden sm:flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Addresses</h2>
                            <button
                                onClick={handleAddAddress}
                                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                Add New Address
                            </button>
                        </div>

                        {/* Mobile layout */}
                        <div className="sm:hidden">
                            <div className="flex justify-between items-center mb-3">
                                <h2 className="text-lg font-semibold text-gray-900">Addresses</h2>
                                <button
                                    onClick={handleAddAddress}
                                    className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                    aria-label="Add address"
                                >
                                    <Plus className="h-5 w-5" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mb-2">
                                Tap + to add a new address
                            </p>
                        </div>
                    </div>
                    {((addresses == null || addresses?.length === 0) && !isAddressesLoading) &&
                        (
                            <div className="py-12 text-center">
                                <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-4 text-lg font-medium text-gray-900">No addresses</h3>
                                <p className="mt-2 text-sm text-gray-500">
                                    You haven't added any addresses yet.
                                </p>
                                <button
                                    onClick={() => setShowAddressForm(true)}
                                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                                >
                                    Add Your First Address
                                </button>
                            </div>
                        )
                    }
                    {isAddressesLoading && (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-gray-600">Loading your addresses...</span>
                        </div>
                    )}
                    <div className="space-y-4">
                        {sortedAddresses?.map((address) => (
                            <AddressCard
                                key={address.id}
                                address={address}
                                onEdit={() => handleEditAddress(address)}
                                onDelete={() => handleDeleteAddress(address.id)}
                                onSetDefault={() => setDefaultAddress(address.id)}
                                onUpdateCoordinates={() => handleUpdateCoordinates(address.id)}
                                isDefault={address.is_default}
                            />
                        ))}
                    </div>
                </div>

                {/* Add Address Form (modal or inline) */}
                {showAddressForm && (
                    <AddressForm
                        onSubmit={handleSubmitAddress}
                        cities={citiesData}
                        initialData={editingAddress || undefined}
                        onCancel={handleCancelAddress}
                        onSkipHouseNumber={() => { }}
                    />
                )}
            </div>
        </div>
    );
};

export default AddressesTab;